import type { RowData } from "@tanstack/react-table";
import { fullDateFullYearFormat } from "@common/helpers/date";
import type { WorkspaceTimezone } from "@common/workspaceTimezone";
import { formatDateOnly } from "@helpers/date";
import { CellText } from "./CellText";
import { EmptyCellValue } from "./EmptyCellValue";
import { getTimestampParts } from "./listDisplayValues";
import type { DataGridCellProps } from "./types";
import styles from "./cellContent.module.scss";

interface TimestampCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    timeZone: (row: TData) => WorkspaceTimezone;
}

/**
 * Timestamp column cell: the day the instant falls on in the row's workspace
 * zone, like a date cell ("Mar 1"), with the full day and time there
 * ("Mar 1, 2026, 1:30 AM") in a tooltip. Search skips timestamps, so the
 * text is never highlighted.
 */
export const TimestampCell = <TData extends RowData>({
    context,
    meta,
    timeZone,
}: Readonly<TimestampCellProps<TData>>) => {
    const value = context.getValue();
    const parts =
        typeof value === "string" || value instanceof Date
            ? getTimestampParts({
                  value,
                  timeZone: timeZone(context.row.original),
              })
            : null;
    if (parts === null) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    const text = formatDateOnly(parts.dateOnly);
    return (
        <CellText
            text={text}
            overflow={meta.overflow}
            tone={meta.tone}
            tooltip={`${formatDateOnly(parts.dateOnly, fullDateFullYearFormat)}, ${parts.time}`}
        >
            <span className={styles.numeric}>{text}</span>
        </CellText>
    );
};
